import React, { useState, useEffect } from 'react';
import AdminSideMenu from '../../components/AdminSideMenu';
import api from "../../utils/axiosInstance";
import * as XLSX from 'xlsx';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

// Utility: check if date in date range (strings or Date objects)
function isDateInRange(check, from, to) {
    if (!check) return false;
    const d = typeof check === "string" ? new Date(check) : check;
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
    return true;
}

const Revenue = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Date filters
    const [dates, setDates] = useState({
        totalRevenue: { from: '', to: '' },
        detailed: { from: '', to: '' },
        notsub: { from: '', to: '' },
        deactivated: { from: '', to: '' },
    });

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [subsRes, carrierRes, pkgRes] = await Promise.all([
                    api.get('/packageSubscribed/all-subscriptions'),
                    api.get('/carriers?limit=10000'),
                    api.get('/packages')
                ]);
                setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : []);
                setCarriers(Array.isArray(carrierRes.data.carriers) ? carrierRes.data.carriers : []);
                setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
            } catch (e) {
                setSubscriptions([]);
                setCarriers([]);
                setPackages([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // 1. Total Revenue breakdown (package-wise, filter by createdAt)
    const totalRevenueSubsInRange = subscriptions.filter(s =>
        isDateInRange(s.createdAt, dates.totalRevenue.from, dates.totalRevenue.to)
    );

    const packageRevenueRows = packages.map(pkg => {
        // Get all subscriptions for this package
        const pkgSubs = totalRevenueSubsInRange.filter(s => String(s.packageId) === String(pkg._id));

        // Count subscriptions without coupon
        const subsWithoutCoupon = pkgSubs.filter(s => !s.hasCoupon);
        const countWithoutCoupon = subsWithoutCoupon.length;
        const revenueWithoutCoupon = subsWithoutCoupon.reduce((sum, s) => sum + (s.actualAmountPaid || pkg.price || 0), 0);

        // Count subscriptions with coupon
        const subsWithCoupon = pkgSubs.filter(s => s.hasCoupon);
        const countWithCoupon = subsWithCoupon.length;
        const revenueWithCoupon = subsWithCoupon.reduce((sum, s) => sum + (s.actualAmountPaid || 0), 0);
        const totalDiscountGiven = subsWithCoupon.reduce((sum, s) => {
            const discount = s.discountType === 'percent'
                ? (s.originalPrice * s.discountAmount / 100)
                : s.discountAmount;
            return sum + discount;
        }, 0);

        // Total calculations
        const totalPackagesSold = pkgSubs.length;
        const totalRevenue = revenueWithoutCoupon + revenueWithCoupon;

        return {
            "Package Name": pkg.name,
            "Packages Sold": totalPackagesSold,
            "Package Price": pkg.price || 0,
            "Subscribed Without Coupon": countWithoutCoupon,
            "Subscribed With Coupon": countWithCoupon,
            "Discount Given": totalDiscountGiven.toFixed(2),
            "Total Revenue": totalRevenue.toFixed(2)
        };
    });

    // 2. Detailed Report of Package Subscriptions
    const detailedSubscriptionRows = subscriptions
        .filter(s => {
            // If no date range selected, include all
            if (!dates.detailed.from && !dates.detailed.to) return true;
            return isDateInRange(s.createdAt, dates.detailed.from, dates.detailed.to);
        })
        .map(sub => {
            // Find carrier details
            const carrier = carriers.find(c => String(c._id) === String(sub.carrierId));
            // Find package details
            const pkg = packages.find(p => String(p._id) === String(sub.packageId));

            return {
                "Carrier Name": carrier?.companyName || "N/A",
                "Register Date": carrier?.createdAt ? format(new Date(carrier.createdAt), "MM/dd/yyyy") : "N/A",
                "Phone No": carrier?.phone || "N/A",
                "Fleety Id": carrier?.identificationNumber || "N/A",
                "Subscribed Package": pkg?.name || "N/A",
                "Amount": pkg?.price || 0,
                "Start Date": sub.createdAt ? format(new Date(sub.createdAt), "MM/dd/yyyy") : "N/A",
                "Status": sub.status && String(sub.status).toLowerCase() === "active" ? "Active" : "Inactive"
            };
        });

    // 3. Carriers Registered but Not Subscribed
    const carriersWithSubs = new Set(
        subscriptions
            .filter(s => s.carrierId)
            .map(s => String(s.carrierId?._id || s.carrierId))
    );

    const notSubscribedCarriersFiltered = carriers.filter(
        c => !carriersWithSubs.has(String(c._id))
    );

    const notSubscribedCarriers = notSubscribedCarriersFiltered
        .filter(c => {
            if (!dates.notsub.from && !dates.notsub.to) return true;
            return isDateInRange(c.createdAt, dates.notsub.from, dates.notsub.to);
        })
        .map(c => ({
            "Date of Registration": c.createdAt ? format(new Date(c.createdAt), "MM/dd/yyyy") : "N/A",
            "Carrier Name": c.companyName || "N/A",
            "Phone No": c.phone || "N/A",
            "Fleety ID": c.identificationNumber || "N/A"
        }));

    // 4. Carriers with Deactivated Subscriptions (status !== "Active")
    const deactivatedSubsFiltered = subscriptions.filter(s =>
        s.status && String(s.status).toLowerCase() !== "active"
    );

    const deactivatedSubs = deactivatedSubsFiltered
        .filter(sub => {
            // If no date range selected, include all
            if (!dates.deactivated.from && !dates.deactivated.to) return true;
            // Filter by updatedAt (when status changed) instead of createdAt
            return isDateInRange(sub.updatedAt, dates.deactivated.from, dates.deactivated.to);
        })
        .map(sub => {
            // Find carrier details
            const carrier = carriers.find(c => String(c._id) === String(sub.carrierId));
            // Find package details
            const pkg = packages.find(p => String(p._id) === String(sub.packageId));

            return {
                "Date of Registration": carrier?.createdAt ? format(new Date(carrier.createdAt), "MM/dd/yyyy") : "N/A",
                "Carrier Name": carrier?.companyName || "N/A",
                "Phone No": carrier?.phone || "N/A",
                "Fleety ID": carrier?.identificationNumber || "N/A",
                "Package Name": pkg?.name || "N/A",
                "Amount": pkg?.price || 0,
                "Start Date": sub.createdAt ? format(new Date(sub.createdAt), "MM/dd/yyyy") : "N/A",
                "Status": sub.status || "Inactive",
                "Date of Expiry": sub.updatedAt ? format(new Date(sub.updatedAt), "MM/dd/yyyy") : "N/A"
            };
        });

    // Export handlers
    const handleExportClick = (rows, sheetName = 'Report') => {
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${sheetName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleCsvClick = (section, rows) => handleExportClick(rows, section + '_CSV');
    const handleExcelClick = (section, rows) => handleExportClick(rows, section + '_EXCEL');

    const handleDateChange = (section, type, value) => {
        setDates(prev => ({
            ...prev,
            [section]: { ...prev[section], [type]: value }
        }));
    };

    return (
        <div className="flex min-h-screen bg-gray-100">
            <AdminSideMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
            <div className="md:ml-64 min-h-screen w-full bg-gray-50 p-4 md:p-8">
                <h1 className="text-3xl font-extrabold mb-8 text-gray-900">Revenue Report</h1>
                <div className="bg-white rounded-xl shadow p-8">
                    {/* 1. Total Revenue */}
                    <section className="mb-8">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                            <span className="font-semibold text-gray-900 text-lg mb-0 md:w-1/3">Total Revenue by Package</span>
                            <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-2 flex-1">
                                <div className="flex items-center gap-2">
                                    <DatePicker
                                        selected={dates.totalRevenue.from ? new Date(dates.totalRevenue.from) : null}
                                        onChange={(date) =>
                                            handleDateChange(
                                                "totalRevenue",
                                                "from",
                                                date ? format(date, "MM/dd/yyyy") : ""
                                            )
                                        }
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="From"
                                        className="border px-3 py-1 rounded cursor-pointer bg-white"
                                        onFocus={(e) => e.target.blur()}
                                    />

                                    <span className="text-gray-600">to</span>

                                    <DatePicker
                                        selected={dates.totalRevenue.to ? new Date(dates.totalRevenue.to) : null}
                                        onChange={(date) =>
                                            handleDateChange(
                                                "totalRevenue",
                                                "to",
                                                date ? format(date, "MM/dd/yyyy") : ""
                                            )
                                        }
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="To"
                                        className="border px-3 py-1 rounded cursor-pointer bg-white"
                                        onFocus={(e) => e.target.blur()}
                                        shouldCloseOnSelect={true}
                                        minDate={dates.totalRevenue.from ? new Date(dates.totalRevenue.from) : null} // restrict past dates
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-gray-700 text-xs md:text-sm mb-1 font-medium">Download report for this section</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleCsvClick('Total_Revenue', packageRevenueRows)} className="bg-blue-500 text-white px-4 py-2 rounded shadow">CSV</button>
                                        <button onClick={() => handleExcelClick('Total_Revenue', packageRevenueRows)} className="bg-blue-500 text-white px-4 py-2 rounded shadow">EXCEL</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <hr className="my-8 border-t border-gray-300" />
                    {/* 2. Detailed Report */}
                    <section className="mb-8">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                            <span className="font-semibold text-gray-900 text-lg mb-0 md:w-1/3">Detailed Report of Package Subscriptions</span>
                            <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-2 flex-1">
                                <div className="flex items-center gap-2">
                                    <DatePicker
                                        selected={dates.detailed.from ? new Date(dates.detailed.from) : null}
                                        onChange={(date) => handleDateChange('detailed', 'from', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="From"
                                        className="border px-3 py-1 rounded"
                                    />
                                    <span className="text-gray-600">to</span>
                                    <DatePicker
                                        selected={dates.detailed.to ? new Date(dates.detailed.to) : null}
                                        onChange={(date) => handleDateChange('detailed', 'to', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="To"
                                        className="border px-3 py-1 rounded"
                                        minDate={dates.detailed.from ? new Date(dates.detailed.from) : null} // restrict past dates
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-gray-700 text-xs md:text-sm mb-1 font-medium">Download report for this section</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleCsvClick('Detailed_Report', detailedSubscriptionRows)} className="bg-blue-500 text-white px-4 py-2 rounded shadow">CSV</button>
                                        <button onClick={() => handleExcelClick('Detailed_Report', detailedSubscriptionRows)} className="bg-blue-500 text-white px-4 py-2 rounded shadow">EXCEL</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <hr className="my-8 border-t border-gray-300" />
                    {/* 3. Registered but not subscribed */}
                    <section className="mb-8">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                            <span className="font-semibold text-gray-900 text-lg mb-0 md:w-1/3">Carriers Registered but not Subscribed</span>
                            <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-2 flex-1">
                                <div className="flex items-center gap-2">
                                    <DatePicker
                                        selected={dates.notsub.from ? new Date(dates.notsub.from) : null}
                                        onChange={(date) => handleDateChange('notsub', 'from', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="From"
                                        className="border px-3 py-1 rounded"
                                    />
                                    <span className="text-gray-600">to</span>
                                    <DatePicker
                                        selected={dates.notsub.to ? new Date(dates.notsub.to) : null}
                                        onChange={(date) => handleDateChange('notsub', 'to', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="To"
                                        className="border px-3 py-1 rounded"
                                        minDate={dates.notsub.from ? new Date(dates.notsub.from) : null} // restrict past dates
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-gray-700 text-xs md:text-sm mb-1 font-medium">Download report for this section</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => {
                                            handleCsvClick('Non Subscribed Carriers', notSubscribedCarriers);
                                        }} className="bg-blue-500 text-white px-4 py-2 rounded shadow">CSV</button>
                                        <button onClick={() => {
                                            handleExcelClick('Non Subscribed Carriers', notSubscribedCarriers);
                                        }} className="bg-blue-500 text-white px-4 py-2 rounded shadow">EXCEL</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <hr className="my-8 border-t border-gray-300" />
                    {/* 4. Deactivated Subscriptions */}
                    <section>
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                            <span className="font-semibold text-gray-900 text-lg mb-0 md:w-1/3">Carriers Deactivated Subscriptions</span>
                            <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-2 flex-1">
                                <div className="flex items-center gap-2">
                                    <DatePicker
                                        selected={dates.deactivated.from ? new Date(dates.deactivated.from) : null}
                                        onChange={(date) => handleDateChange("deactivated", "from", date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="From"
                                        className="border px-3 py-1 rounded"
                                    />
                                    <span className="text-gray-600">to</span>
                                    <DatePicker
                                        selected={dates.deactivated.to ? new Date(dates.deactivated.to) : null}
                                        onChange={(date) => handleDateChange("deactivated", "to", date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="To"
                                        className="border px-3 py-1 rounded"
                                        minDate={dates.deactivated.from ? new Date(dates.deactivated.from) : null} // restrict past dates
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <span className="text-gray-700 text-xs md:text-sm mb-1 font-medium">Download report for this section</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => {
                                            handleCsvClick('Deactivated_Subscriptions', deactivatedSubs);
                                        }} className="bg-blue-500 text-white px-4 py-2 rounded shadow">CSV</button>
                                        <button onClick={() => {
                                            handleExcelClick('Deactivated_Subscriptions', deactivatedSubs);
                                        }} className="bg-blue-500 text-white px-4 py-2 rounded shadow">EXCEL</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {isMenuOpen && (
                <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsMenuOpen(false)} />
            )}
        </div>
    );
};

export default Revenue;
